import React from "react";
import { useSelector } from "react-redux";
import { Drawer, Row, Col, Divider } from "antd";
import Button from "@dtci/spellbook/Button";
import Select from "@dtci/spellbook/Select";
import Typography from "@dtci/spellbook/Typography";
import Pillbox from "@dtci/spellbook/Pillbox";
import { DetailRow } from "tap/components/DetailRow";
import { ItemCard } from "tap/components/ItemCard";
import { CollapsibleSection } from "tap/components/CollapsibleSection";
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import moment from "moment";
import "./styles.scss";

const STATUS_COLORS = {
  green: "#4caf50",
  orange: "#ffa500",
  red: "#f44336",
  blue: "#2196f3",
  gray: "#9e9e9e",
};

/**
 * Maps job status to display configuration (icon and color).
 * Green: success states, Blue: in-progress, Orange: attention/pending, Red: error states
 */
const getStatusConfig = (status) => {
  const s = (status || "").toUpperCase();

  if (s === "ACTIVE" || s === "COMPLETE" || s === "READY" || s === "CREATED") {
    return { icon: "fa-check", color: STATUS_COLORS.green };
  }
  if (s === "NEEDS_ATTENTION") {
    return { icon: "fa-exclamation", color: STATUS_COLORS.orange };
  }
  if (s === "STARTING" || s === "RUNNING" || s === "VALIDATING") {
    return { icon: "fa-arrows-rotate", color: STATUS_COLORS.blue };
  }
  if (
    s === "ERROR" ||
    s === "INACTIVE" ||
    s === "INACTIVATED" ||
    s === "ABORTED" ||
    s === "INVALID" ||
    s === "FAILED"
  ) {
    return { icon: "fa-xmark", color: STATUS_COLORS.red };
  }
  if (s === "PENDING") {
    return { icon: "fa-pause", color: STATUS_COLORS.orange };
  }
  return { icon: null, color: STATUS_COLORS.gray };
};

const SectionDivider = () => <div className="section-divider" />;

function JobDetails({ data }) {
  if (!data) return null;

  const statusConfig = getStatusConfig(data.status);

  return (
    <div className="job-details">
      <div className="job-details-title-block">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div className="job-details-id">Job #{data.id}</div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 16,
              backgroundColor: statusConfig.color + "20",
              border: `1px solid ${statusConfig.color}40`,
            }}
          >
            {statusConfig.icon && (
              <i
                className={`fas ${statusConfig.icon}`}
                style={{ fontSize: 12, color: statusConfig.color }}
              />
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: statusConfig.color,
                textTransform: "uppercase",
              }}
            >
              {data.status}
            </span>
          </div>
        </div>
        <Typography variant="T40" bold className="job-details-name">
          {data.description || "Untitled Job"}
        </Typography>
      </div>

      <div className="job-details-key-section">
        <DetailRow
          label="Start Date"
          value={
            data.startDate ? moment(data.startDate).format("MM/DD/YYYY") : null
          }
        />
        <DetailRow
          label="End Date"
          value={
            data.endDate ? moment(data.endDate).format("MM/DD/YYYY") : null
          }
        />
        <DetailRow
          label="TPR Job"
          value={data.isTpr ? "Yes" : "No"}
          noBorder={!data.isTpr}
        />
        {data.isTpr && (
          <>
            <DetailRow label="RL Contact ID" value={data.rlContactId} />
            <DetailRow label="RL Contact Name" value={data.rlContactName} />
            <DetailRow
              label="PMX Release ID"
              value={data.pmxReleaseId}
              noBorder
            />
          </>
        )}
      </div>

      <CollapsibleSection
        title="Catalogs"
        count={data?.catalogs?.length || 0}
        defaultOpen={true}
      >
        {data?.catalogs?.length > 0 ? (
          data.catalogs.map((catalog, idx) => (
            <ItemCard key={idx}>
              <DetailRow label="Name" value={catalog.name} />
              <DetailRow label="ID" value={catalog.id} noBorder />
            </ItemCard>
          ))
        ) : (
          <div className="empty-state">No catalogs assigned</div>
        )}
      </CollapsibleSection>

      <SectionDivider />

      <CollapsibleSection
        title="Deals"
        count={data?.deals?.length || 0}
        defaultOpen={true}
      >
        {data?.deals?.length > 0 ? (
          data.deals.map((deal, idx) => (
            <ItemCard key={idx}>
              <DetailRow label="Name" value={deal.name} />
              <DetailRow label="ID" value={deal.id} noBorder />
            </ItemCard>
          ))
        ) : (
          <div className="empty-state">No deals assigned</div>
        )}
      </CollapsibleSection>

      <SectionDivider />

      <CollapsibleSection
        title="Media"
        count={data?.medias?.length || 0}
        defaultOpen={true}
      >
        {data?.medias?.length > 0 ? (
          <div className="pillbox-container">
            {data.medias.map((media, idx) => (
              <Pillbox
                key={idx}
                size="small"
                color="primary"
                light
                showClose={false}
              >
                {media.name}
              </Pillbox>
            ))}
          </div>
        ) : (
          <div className="empty-state">No media assigned</div>
        )}
      </CollapsibleSection>

      <SectionDivider />

      <CollapsibleSection
        title="Territories"
        count={data?.territories?.length || 0}
        defaultOpen={true}
      >
        {data?.territories?.length > 0 ? (
          <div className="pillbox-container">
            {data.territories.map((territory, idx) => (
              <Pillbox
                key={idx}
                size="small"
                color="primary"
                light
                showClose={false}
              >
                {territory.name}
              </Pillbox>
            ))}
          </div>
        ) : (
          <div className="empty-state">No territories assigned</div>
        )}
      </CollapsibleSection>
    </div>
  );
}

export const ViewLicensingJobDrawer = () => {
  const {
    isJobViewDrawerOpen,
    selectedJob,
    selectedJobHistory,
    selectedHistory,
  } = useSelector((state) => state.licensingJobs);
  const { updateJobs } = useJobsActions();

  const handleClose = () => {
    updateJobs({ isJobViewDrawerOpen: false });
  };

  const handleAfterOpenChange = (open) => {
    if (!open) {
      updateJobs({
        selectedJob: null,
        selectedJobHistory: [],
        selectedHistory: "",
      });
    }
  };

  const drawerHeader = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 20 }}>Licensing Job</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Select
          placeholder="History"
          options={selectedJobHistory}
          isClearable
          isSearchable
          onChange={(value) => updateJobs({ selectedHistory: value })}
          value={selectedHistory}
        />
      </div>
    </div>
  );

  const showHistory = selectedHistory && selectedHistory.data;
  const drawerWidth = showHistory ? 1000 : 750;

  return (
    <Drawer
      className="create-drawer"
      title={drawerHeader}
      width={drawerWidth}
      placement="right"
      open={isJobViewDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div
          className="button-row"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Button
            iconType="fa-solid fa-xmark"
            labelText="close"
            styleType="link"
            onClick={handleClose}
          >
            Close
          </Button>
        </div>
      }
    >
      <Row gutter={32} align="top" style={{ display: "flex" }}>
        <Col
          span={showHistory ? 11 : 24}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <JobDetails data={selectedJob} />
        </Col>
        {showHistory && (
          <>
            <Col
              span={2}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "stretch",
                padding: 0,
              }}
            >
              <Divider
                type="vertical"
                style={{
                  height: "100%",
                  minHeight: "60vh",
                  background: "#d9d9d9",
                  margin: 0,
                }}
              />
            </Col>
            <Col span={11} style={{ display: "flex", flexDirection: "column" }}>
              <JobDetails data={selectedHistory.data} />
            </Col>
          </>
        )}
      </Row>
    </Drawer>
  );
};
